// src/hooks/useNotifications.ts - Hook pour gérer les notifications (TEMPS RÉEL)

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from './usePermissions';
import { useCurrentEmployee } from './useEmployees';
import { useSites } from './useSites';
import NotificationService from '../services/notificationService';
import { Notification, NotificationSummary, NotificationType } from '../types/notifications';
import { message } from 'antd';
import { collection, query, where, orderBy, limit as firestoreLimit, onSnapshot, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { playNotificationSound, startRepeatingSound, stopRepeatingSound } from '../utils/notificationSound';
import { PaymentPlan } from '../types/paymentPlans';
import {
  buildPaymentPlanReminderMessage,
  getPaymentPlanReminderKind,
  getPaymentPlanReminderSeverity,
  getPaymentPlanReminderType,
  isPaymentPlanEligibleForReminder,
  PAYMENT_PLAN_REMINDER_TYPES,
  startOfLocalDay,
} from '../utils/paymentPlanReminders';
import { isFeatureEnabled } from '../types/business';
import i18n from '../i18n';
import { getErrorMessage } from '../utils/i18n/getErrorMessage';
import {
  canReceiveStockSiteNotification,
  isStockAlertNotificationType,
  isTrueMultiSiteLayout,
} from '../utils/stockNotificationRecipients';

/**
 * Hook pour récupérer les notifications en TEMPS RÉEL
 */
export const useNotifications = (options: {
  unreadOnly?: boolean;
  limit?: number;
  type?: NotificationType;
} = {}) => {
  const { currentBusiness, user, isSuperAdmin, isCSM } = useAuth();
  const businessId = currentBusiness?.id;
  const userId = user?.uid;
  const { data: currentEmployee } = useCurrentEmployee();
  const { data: sites = [] } = useSites(businessId);
  const isTrueMultiSite = useMemo(() => isTrueMultiSiteLayout(sites), [sites]);
  const assignedSiteIds = currentEmployee?.assignedSiteIds;
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const previousCountRef = useRef<number>(0);
  const isFirstLoadRef = useRef(true);

  useEffect(() => {
    console.log('🔔 [useNotifications] Setup listener temps réel');
    
    if (!businessId || !userId) {
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    // ✅ PHASE 1.4 : Pause listener si l'onglet est en arrière-plan
    if (document.hidden) {
      console.log('⏸️ [useNotifications] Onglet inactif, listener en pause');
      setNotifications([]);
      setIsLoading(false);
      return;
    }

    try {
      let q = query(
        collection(db, COLLECTIONS.notifications),
        where('businessId', '==', businessId),
        orderBy('timestamp', 'desc')
      );

      // Appliquer les filtres
      if (options.unreadOnly) {
        q = query(q, where('read', '==', false));
      }
      if (options.type) {
        q = query(q, where('type', '==', options.type));
      }
      if (options.limit) {
        q = query(q, firestoreLimit(options.limit));
      } else {
        q = query(q, firestoreLimit(50)); // Limite par défaut
      }

      // ✅ PHASE 1.4 : Listener temps réel optimisé (pas de metadata changes)
      const unsubscribe = onSnapshot(
        q,
        { includeMetadataChanges: false }, // Réduire les mises à jour inutiles
        (snapshot) => {
          console.log(`🔔 [useNotifications] Snapshot: ${snapshot.size} notification(s)`);
          
          const results: Notification[] = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              type: data.type as NotificationType,
              severity: data.severity,
              businessId: data.businessId,
              message: data.message,
              timestamp: data.timestamp?.toDate() || data.createdAt?.toDate() || new Date(),
              read: data.read || false,
              triggeredBy: data.triggeredBy,
              triggeredByUser: data.triggeredByUser,
              recipientUserId: data.recipientUserId,
              audience: data.audience,
              productId: data.productId,
              productName: data.productName,
              currentStock: data.currentStock,
              minStockAlert: data.minStockAlert,
              siteId: data.siteId,
              siteName: data.siteName,
              clientId: data.clientId,
              clientName: data.clientName,
              appointmentId: data.appointmentId,
              appointmentRequestId: data.appointmentRequestId,
              pickupRequestId: data.pickupRequestId,
              trackingId: data.trackingId,
              supportTicketId: data.supportTicketId,
              supportTicketSubject: data.supportTicketSubject,
              appointmentDate: data.appointmentDate?.toDate(),
              appointmentTime: data.appointmentTime,
              appointmentTitle: data.appointmentTitle,
              employeeName: data.employeeName,
              paymentPlanId: data.paymentPlanId,
              targetDate: data.targetDate?.toDate(),
              amountRemaining: data.amountRemaining,
              saleReceiptNumber: data.saleReceiptNumber,
              activityReportId: data.activityReportId,
              employeeId: data.employeeId,
              manufacturingOrderId: data.manufacturingOrderId,
              inventorySessionId: data.inventorySessionId,
              followUpMessageId: data.followUpMessageId,
              dedupKey: data.dedupKey,
            };
          }).filter((n) => {
            if (n.audience === 'nauvatek_staff') {
              return isSuperAdmin || isCSM;
            }
            if (n.audience === 'user' || n.recipientUserId) {
              return n.recipientUserId === userId;
            }
            if (isStockAlertNotificationType(n.type)) {
              return canReceiveStockSiteNotification({
                isTrueMultiSite,
                siteId: n.siteId,
                assignedSiteIds,
              });
            }
            return true;
          });
          
          // Détecter les nouvelles notifications NON LUES
          const unreadResults = results.filter(n => !n.read);
          const currentUnreadCount = unreadResults.length;
          const previousUnreadCount = previousCountRef.current;

          // Si ce n'est pas le premier chargement ET qu'il y a plus de notifications non lues
          if (!isFirstLoadRef.current && currentUnreadCount > previousUnreadCount) {
            const newNotificationsCount = currentUnreadCount - previousUnreadCount;
            console.log(`🆕 [useNotifications] ${newNotificationsCount} nouvelle(s) notification(s)`);
            
            // Récupérer la dernière notification non lue pour afficher les détails
            const latestNotification = unreadResults[0];
            
            if (latestNotification) {
              // Jouer le son de notification
              playNotificationSound();
            }
          }

          // Gérer la répétition du son pour les demandes de RDV non lues
          const unreadAppointmentRequests = unreadResults.filter(
            n => n.type === 'appointment_request'
          );

          if (unreadAppointmentRequests.length > 0) {
            // Il y a des demandes de RDV non traitées → démarrer la répétition
            startRepeatingSound(1); // Toutes les 1 minute (urgent pour les RDV)
            console.log(`🔁 [useNotifications] ${unreadAppointmentRequests.length} demande(s) de RDV non traitée(s) → répétition activée`);
          } else {
            // Plus de demandes de RDV non traitées → arrêter la répétition
            stopRepeatingSound();
          }

          // Mettre à jour les refs
          previousCountRef.current = currentUnreadCount;
          if (isFirstLoadRef.current) {
            isFirstLoadRef.current = false;
          }
          
          setNotifications(results);
          setIsLoading(false);
          setError(null);
        },
        (err) => {
          console.error('❌ [useNotifications] Erreur:', err);
          setError(err as Error);
          setIsLoading(false);
        }
      );

      // ✅ PHASE 1.4 : Écouter les changements de visibilité de l'onglet
      const handleVisibilityChange = () => {
        if (document.hidden) {
          console.log('⏸️ [useNotifications] Onglet caché, déconnexion listener');
          unsubscribe();
          stopRepeatingSound();
        } else {
          console.log('▶️ [useNotifications] Onglet visible, reconnexion listener');
          // Le useEffect se relancera automatiquement
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        console.log('🔌 [useNotifications] Déconnexion listener');
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        stopRepeatingSound(); // Arrêter la répétition lors du démontage
        unsubscribe();
      };
    } catch (err: any) {
      console.error('❌ [useNotifications] Erreur setup:', err);
      setError(err);
      setIsLoading(false);
    }
  }, [
    businessId,
    userId,
    isSuperAdmin,
    isCSM,
    isTrueMultiSite,
    assignedSiteIds,
    options.unreadOnly,
    options.limit,
    options.type,
  ]);

  return { data: notifications, isLoading, error };
};

/**
 * Hook pour calculer le résumé des notifications en TEMPS RÉEL
 */
export const useNotificationSummary = () => {
  const { data: notifications = [] } = useNotifications({ limit: 200 });
  
  const summary = useMemo((): NotificationSummary => {
    return {
      total: notifications.length,
      unread: notifications.filter(n => !n.read).length,
      byType: {
        low_stock: notifications.filter(n => n.type === 'low_stock').length,
        out_of_stock: notifications.filter(n => n.type === 'out_of_stock').length,
        appointment_reminder_24h: notifications.filter(n => n.type === 'appointment_reminder_24h').length,
        appointment_reminder_2h: notifications.filter(n => n.type === 'appointment_reminder_2h').length,
        appointment_request: notifications.filter(n => n.type === 'appointment_request').length,
        pickup_request: notifications.filter(n => n.type === 'pickup_request').length,
        support_ticket_created: notifications.filter(n => n.type === 'support_ticket_created').length,
        support_ticket_reply: notifications.filter(n => n.type === 'support_ticket_reply').length,
        support_ticket_resolved: notifications.filter(n => n.type === 'support_ticket_resolved').length,
        support_ticket_opened_for_you: notifications.filter(n => n.type === 'support_ticket_opened_for_you').length,
        birthday: notifications.filter(n => n.type === 'birthday').length,
        inactive_client: notifications.filter(n => n.type === 'inactive_client').length,
        loyalty_milestone: notifications.filter(n => n.type === 'loyalty_milestone').length,
        payment_plan_reminder_2d: notifications.filter(n => n.type === 'payment_plan_reminder_2d').length,
        payment_plan_reminder_1d: notifications.filter(n => n.type === 'payment_plan_reminder_1d').length,
        payment_plan_reminder_due: notifications.filter(n => n.type === 'payment_plan_reminder_due').length,
        activity_report_submit_reminder: notifications.filter(n => n.type === 'activity_report_submit_reminder').length,
        activity_report_validated: notifications.filter(n => n.type === 'activity_report_validated').length,
        activity_report_overdue: notifications.filter(n => n.type === 'activity_report_overdue').length,
        payment_plan_overdue: notifications.filter(n => n.type === 'payment_plan_overdue').length,
        licence_payment_due_7d: notifications.filter(n => n.type === 'licence_payment_due_7d').length,
        licence_payment_due_1d: notifications.filter(n => n.type === 'licence_payment_due_1d').length,
        licence_payment_blocked: notifications.filter(n => n.type === 'licence_payment_blocked').length,
        messages_clients_ready: notifications.filter(n => n.type === 'messages_clients_ready').length,
        messages_clients_no_phone: notifications.filter(n => n.type === 'messages_clients_no_phone').length,
        manufacturing_order_on_hold: notifications.filter(n => n.type === 'manufacturing_order_on_hold').length,
        manufacturing_order_done: notifications.filter(n => n.type === 'manufacturing_order_done').length,
        inventory_audit_started: notifications.filter(n => n.type === 'inventory_audit_started').length,
        inventory_audit_to_close: notifications.filter(n => n.type === 'inventory_audit_to_close').length,
        inventory_loss_created: notifications.filter(n => n.type === 'inventory_loss_created').length,
        performance_sheets_generated: notifications.filter(n => n.type === 'performance_sheets_generated').length,
        performance_sheet_validated: notifications.filter(n => n.type === 'performance_sheet_validated').length,
      },
      bySeverity: {
        warning: notifications.filter(n => n.severity === 'warning').length,
        critical: notifications.filter(n => n.severity === 'critical').length,
        info: notifications.filter(n => n.severity === 'info').length,
      },
    };
  }, [notifications]);

  return { data: summary, isLoading: false, error: null };
};

/**
 * Hook pour marquer une notification comme lue
 */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (notificationId: string) => {
      await NotificationService.markAsRead(notificationId);
    },
    onSuccess: () => {
      // Invalider les caches des notifications
      queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'summary', businessId] });
    },
  });
};

/**
 * Hook pour marquer toutes les notifications comme lues
 */
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error('Business ID requis');
      await NotificationService.markAllAsRead(businessId);
    },
    onSuccess: () => {
      message.success(i18n.t('toasts.markAllReadSuccess', { ns: 'notifications' }));
      // Invalider les caches des notifications
      queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'summary', businessId] });
    },
    onError: (error) => {
      console.error('Erreur lors du marquage des notifications:', error);
      message.error(
        getErrorMessage(error) || i18n.t('toasts.markAllReadError', { ns: 'notifications' })
      );
    },
  });
};

/**
 * Hook pour supprimer une notification
 */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (notificationId: string) => {
      await NotificationService.deleteNotification(notificationId);
    },
    onSuccess: () => {
      message.success(i18n.t('toasts.deleteSuccess', { ns: 'notifications' }));
      // Invalider les caches des notifications
      queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'summary', businessId] });
    },
    onError: (error) => {
      console.error('Erreur lors de la suppression:', error);
      message.error(getErrorMessage(error) || i18n.t('toasts.deleteError', { ns: 'notifications' }));
    },
  });
};

/**
 * Hook pour créer une notification de stock
 */
export const useCreateStockNotification = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, user } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (data: {
      productId: string;
      productName: string;
      currentStock: number;
      minStockAlert: number;
      siteId?: string;
      siteName?: string;
    }) => {
      if (!businessId) throw new Error('Business ID requis');
      
      return NotificationService.createStockNotification({
        businessId,
        productId: data.productId,
        productName: data.productName,
        currentStock: data.currentStock,
        minStockAlert: data.minStockAlert,
        siteId: data.siteId,
        siteName: data.siteName,
        triggeredBy: user?.uid,
        triggeredByUser: user?.displayName || user?.email || 'Utilisateur',
      });
    },
    onSuccess: () => {
      // Invalider les caches des notifications
      queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'summary', businessId] });
    },
    onError: (error) => {
      console.error('Erreur lors de la création de la notification:', error);
    },
  });
};

/**
 * Hook pour créer une notification de rendez-vous
 */
export const useCreateAppointmentNotification = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, user } = useAuth();
const businessId = currentBusiness?.id;

  return useMutation<string, Error, {
    appointmentId: string;
    clientId: string;
    clientName: string;
    appointmentTitle: string;
    appointmentDate: Date;
    appointmentTime: string;
    employeeName?: string;
    reminderType: '24h' | '2h';
  }>({
    mutationFn: async (notificationData) => {
      if (!businessId) throw new Error('Business ID requis');
      
      return NotificationService.createAppointmentNotification({
        businessId,
        appointmentId: notificationData.appointmentId,
        clientId: notificationData.clientId,
        clientName: notificationData.clientName,
        appointmentTitle: notificationData.appointmentTitle,
        appointmentDate: notificationData.appointmentDate,
        appointmentTime: notificationData.appointmentTime,
        employeeName: notificationData.employeeName,
        reminderType: notificationData.reminderType,
        triggeredBy: user?.uid,
        triggeredByUser: user?.displayName || user?.email || 'Utilisateur',
      });
    },
    onSuccess: () => {
      // Invalider les caches des notifications
      queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'summary', businessId] });
    },
    onError: (error) => {
      console.error('Erreur lors de la création de la notification de rendez-vous:', error);
    },
  });
};

/**
 * Hook pour créer une notification d'anniversaire
 */
export const useCreateBirthdayNotification = () => {
  const queryClient = useQueryClient();
  const { currentBusiness, user } = useAuth();
const businessId = currentBusiness?.id;

  return useMutation<string, Error, {
    clientId: string;
    clientName: string;
  }>({
    mutationFn: async (notificationData) => {
      if (!businessId) throw new Error('Business ID requis');
      
      return NotificationService.createBirthdayNotification({
        businessId,
        clientId: notificationData.clientId,
        clientName: notificationData.clientName,
        triggeredBy: user?.uid,
        triggeredByUser: user?.displayName || user?.email || 'Utilisateur',
      });
    },
    onSuccess: () => {
      // Invalider les caches des notifications
      queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'summary', businessId] });
    },
    onError: (error) => {
      console.error('Erreur lors de la création de la notification d\'anniversaire:', error);
    },
  });
};

/**
 * Hook pour vérifier les anniversaires du jour et créer des notifications
 */
export const useCheckBirthdays = () => {
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  const createBirthdayNotification = useCreateBirthdayNotification();

  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error('Business ID requis');

      // Récupérer tous les clients avec une date de naissance
      const clientsQuery = query(
        collection(db, COLLECTIONS.clients),
        where('businessId', '==', businessId),
        where('birthDate', '!=', null)
      );

      const clientsSnapshot = await getDocs(clientsQuery).catch(error => {
        handleFirestoreError(error, 'useNotifications - Clients query');
        throw error;
      });
      const today = new Date();
      // Utiliser UTC pour éviter les problèmes de fuseau horaire
      const todayMonth = today.getUTCMonth();
      const todayDate = today.getUTCDate();

      const birthdayClients = clientsSnapshot.docs.filter(doc => {
        const clientData = doc.data();
        const birthDate = clientData.birthDate?.toDate();
        
        console.log('🔍 DEBUG Client:', {
          name: clientData.name,
          birthDate: birthDate,
          birthMonth: birthDate?.getUTCMonth(),
          birthDay: birthDate?.getUTCDate(),
          todayMonth,
          todayDate,
          isMatch: birthDate && birthDate.getUTCMonth() === todayMonth && birthDate.getUTCDate() === todayDate
        });
        
        if (!birthDate) return false;
        
        // Vérifier si c'est l'anniversaire aujourd'hui (même jour et mois en UTC)
        return birthDate.getUTCMonth() === todayMonth && birthDate.getUTCDate() === todayDate;
      });

      // Créer des notifications pour chaque client qui a son anniversaire
      const notificationPromises = birthdayClients.map(async (doc) => {
        const clientData = doc.data();
        try {
          await createBirthdayNotification.mutateAsync({
            clientId: doc.id,
            clientName: clientData.name,
          });
        } catch (error) {
          console.warn(`Erreur lors de la création de la notification d'anniversaire pour ${clientData.name}:`, error);
        }
      });

      await Promise.all(notificationPromises);
      return birthdayClients.length;
    },
    onSuccess: (birthdayCount) => {
      console.log(`${birthdayCount} notifications d'anniversaire créées`);
    },
    onError: (error) => {
      console.error('Erreur lors de la vérification des anniversaires:', error);
    },
  });
};



export const useCheckPaymentPlanReminders = () => {
  const { currentBusiness } = useAuth();
  const businessId = currentBusiness?.id;
  const permissions = usePermissions();

  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error('Business ID requis');
      if (!isFeatureEnabled(currentBusiness, 'paymentPlans')) return 0;
      if (!permissions?.canViewPaymentPlans) return 0;

      const todayKey = startOfLocalDay(new Date()).toISOString().slice(0, 10);
      const storageKey = `payment_plan_reminders_checked:${businessId}:${todayKey}`;
      if (typeof localStorage !== 'undefined' && localStorage.getItem(storageKey) === '1') {
        return 0;
      }

      const plansQuery = query(
        collection(db, COLLECTIONS.paymentPlans),
        where('businessId', '==', businessId)
      );
      const plansSnapshot = await getDocs(plansQuery).catch((error) => {
        handleFirestoreError(error, 'useCheckPaymentPlanReminders - plans query');
        throw error;
      });

      const now = new Date();
      let createdCount = 0;

      for (const planDoc of plansSnapshot.docs) {
        const data = planDoc.data();
        const plan: PaymentPlan = {
          id: planDoc.id,
          businessId: data.businessId,
          clientId: data.clientId,
          clientName: data.clientName,
          transactionGroupId: data.transactionGroupId || data.saleId || '',
          saleReceiptNumber: data.saleReceiptNumber,
          totalAmount: data.totalAmount,
          amountPaid: data.amountPaid ?? 0,
          amountRemaining: data.amountRemaining ?? 0,
          status: data.status,
          targetDate: data.targetDate?.toDate?.(),
          createdAt: data.createdAt?.toDate?.() ?? new Date(),
          updatedAt: data.updatedAt?.toDate?.() ?? new Date(),
        };

        if (!isPaymentPlanEligibleForReminder(plan) || !plan.targetDate) continue;

        const reminderKind = getPaymentPlanReminderKind(plan.targetDate, now);
        if (!reminderKind) continue;

        const reminderType = getPaymentPlanReminderType(plan.targetDate, now);
        if (!reminderType) continue;

        const message = buildPaymentPlanReminderMessage({
          kind: reminderKind,
          clientName: plan.clientName,
          amountRemaining: plan.amountRemaining,
          saleReceiptNumber: plan.saleReceiptNumber,
          targetDate: plan.targetDate,
        });

        const notificationId = await NotificationService.createPaymentPlanReminderNotification({
          businessId,
          paymentPlanId: plan.id,
          clientId: plan.clientId,
          clientName: plan.clientName,
          targetDate: plan.targetDate,
          amountRemaining: plan.amountRemaining,
          saleReceiptNumber: plan.saleReceiptNumber,
          reminderType,
          message,
          severity: getPaymentPlanReminderSeverity(reminderKind),
          triggeredBy: 'system',
          triggeredByUser: 'Système automatique',
        });

        if (notificationId) createdCount += 1;
      }

      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(storageKey, '1');
      }

      return createdCount;
    },
    onSuccess: (count) => {
      if (count > 0) {
        console.log(`${count} notification(s) d'échéance paiement en plusieurs créée(s)`);
      }
    },
    onError: (error) => {
      console.error('Erreur lors de la vérification des échéances paiement en plusieurs:', error);
    },
  });
};

export { PAYMENT_PLAN_REMINDER_TYPES };

/**
 * Hook pour nettoyer les anciennes notifications
 */
export const useCleanupOldNotifications = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async () => {
      if (!businessId) throw new Error('Business ID requis');
      await NotificationService.cleanupOldNotifications(businessId);
    },
    onSuccess: () => {
      message.success(i18n.t('toasts.cleanupSuccess', { ns: 'notifications' }));
      // Invalider les caches des notifications
      queryClient.invalidateQueries({ queryKey: ['notifications', businessId] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'summary', businessId] });
    },
    onError: (error) => {
      console.error('Erreur lors du nettoyage:', error);
      message.error('Erreur lors du nettoyage des notifications');
    },
  });
};

/**
 * Hook utilitaire pour vérifier si des notifications critiques existent
 */
export const useCriticalNotifications = () => {
  const { data: notifications = [] } = useNotifications({ 
    unreadOnly: true, 
    type: 'out_of_stock' 
  });
  
  return {
    hasCriticalNotifications: notifications.length > 0,
    criticalCount: notifications.length,
    criticalNotifications: notifications,
  };
};
